import React, {Fragment, useEffect, useRef, useState} from "react";
import {Collapse} from "@material-ui/core";
import LinearProgress from "@material-ui/core/LinearProgress";
import {Redirect, Route} from "react-router-dom";
import { BrowserView, MobileView } from "react-device-detect";
import * as moment from "moment";

import {BannerHeader} from "../structure";
import {
	countMap,
	extractCartData,
	formatPrice,
	mapArray,
	mapArrayToObject,
	mapObject,
	mapObjectToArray,
	objLength,
	partitionObject,
} from "../../lib/tools";
import {Button, QtySelector, RelativeSwitch} from "../common/common";
import {Checkbox, FormGroup, FormInput, ValidateForm} from "../common/formValidation";
import {reduxWrapper} from "../../globalState";
import {getImage} from "../../lib/api";
import {CustomOption} from "./storeView";
import {LoginModal} from "../common/modal";

//Icons
import CartIcon from "../../resources/icons/basket-grey.svg";
import SslIcon from "../../resources/icons/ssl.svg";
import CheckWhiteIcon from "../../resources/icons/checkmark-white.svg";
import DropArrowIcon from "../../resources/icons/downArrow-black.svg";
import visaIcon from "../../resources/icons/visa2.svg";
import mastercardIcon from "../../resources/icons/mastercard2.svg";
import mobilepayIcon from "../../resources/icons/mobilepay.svg";

//Images
import bgImages from "../../resources/images/checkoutBg.jpg";

const steps = {
	1: "Levering",
	2: "Betaling",
	3: "Kvittering"
};

const CheckoutLoginRender = ({userId, history, id}) => {
	userId === Number(id) && history.push("/checkout");
	return <LoginModal onHide={() => history.push("/minfest")}/>
};

const CheckoutLogin = reduxWrapper("getUserId")(CheckoutLoginRender);

const CheckoutRender = (
	{
		awaitCart, 
		cacheProduct, 
		apiCall, 
		vendors, 
		cart, 
		products, 
		checkoutCart, 
		payOrder, 
		awaitVendors, 
		history,
		userId
	}) => {
	
	const [loading, setLoading] = useState(true);
	const [shipping, setShipping] = useState({});
	const [orderId, setOrderId] = useState(null);
	const [order, setOrder] = useState(null);
	const [error, setError] = useState(null);
	
	const name = useRef("");
	
	useEffect(() => {
			awaitCart(x => cacheProduct({
				products: mapObjectToArray(x, y => y.productId),
				full: true
			}).then(() => setLoading(false)));
			
			awaitVendors(v => {
				setShipping(mapObject(v, x => x.shipping.length > 0 ? x.shipping[0].id : null));
			
				apiCall("GET", `/party/current/quote/vendors`)
				.then(data => {
					setShipping(p => mapArrayToObject(data, x => ({
						key: x.id,
						data: p[x.id] || null
					})));
				});
			});
	}, []);

	useEffect(() => {
		window.scrollTo(0,0);
	}, []);

	const {total, products: productList} = extractCartData(cart, products);
	const shippingCost = countMap(shipping, (s, k) => {
		if (!s) return 0;
		const vendor = vendors[k];
		if (!vendor) return 0;
		const method = vendor.shipping.find(x => x.id === parseInt(s, 10));
		return method ? parseFloat(method.price) : 0;
	});
	
	const testShipping = () => {
		if (loading) return false;
		for (let key in shipping) {
			if (!shipping.hasOwnProperty(key)) continue;
			if (!shipping[key]) return false;
		}
		return true;
	};
	
	const createOrder = ({separateBillingAddress, email, phone, ...orderInfo}) => {
		if (loading) return;
		
		setLoading(true);
		
		if (!separateBillingAddress)
			orderInfo.billingAddress = orderInfo.mainAddress;
		orderInfo.vendors = shipping;
		
		name.current = orderInfo.mainAddress.name;
		
		checkoutCart({
			data: {...orderInfo, user: {email, phone, name: orderInfo.mainAddress.name}},
			onSuccess: data => {
				setOrderId(data.id);
				history.push("/checkout/betaling");
			}
		})
			.finally(() => setLoading(false));
	};
	
	const onPay = (card) => {
		if (!orderId) {
			history.push("/checkout/levering");
			return;
		}
		
		setLoading(true);

		payOrder({
			data: card,
			order: orderId,
			success: () => {
				history.push("/checkout/kvittering");
				apiCall("GET", `/user/me/order/${orderId}`)
					.then(data => handleOrderInfo(data))
					.finally(setLoading(false))
			},
			onError: () => {setLoading(false); setError(true);}
		});
	};

	const handleOrderInfo = (data) => {
		setOrder(data);

		// Setting up transaction data GTM datalayer
		let transactionProducts = [];
		for (const [, info] of Object.entries(productList)) {
			transactionProducts.push({
				'sku': info.productId,
				'name': info.product.name,
				'price': Number(info.price),
				'quantity': info.qty
			});
		}
		window.dataLayer = window.dataLayer || [];

		// Pushing transaction data to GTM datalayer
		window.dataLayer.push({
			'event': 'purchase',
			'transactionId': data.id,
			'transactionTotal': data.total,
			'transactionTax': (Number(data.productTotal) * 0.2),
			'transactionShipping': Number(data.shippingTotal),
			'transactionProducts': transactionProducts
		})
	}

	const orderProducts = order ? order.products.map(x => ({...x, product: products[x.productId]})) : [];
	
	return (
		<div id="checkoutWrapper">
			<BannerHeader bgImage={bgImages} checkout/>
			<div className="checkoutContentWrapper maxWidth">
				{loading && <LinearProgress/>}

				<RelativeSwitch>
					<Route path="reservation/:userId" render={({match: {params: {userId}}}) => <CheckoutLogin id={userId}/>}/>
					<Route path="levering" render={() => 
						<Shipping
							products={productList}
							shipping={shippingCost}
							total={total}
							onSubmit={createOrder}
							apiCall={apiCall}
							vendors={vendors}
							shippingNext={() => testShipping()}
							userId={userId}
						/>
					}/>
					<Route path="betaling" render={() => 
						orderId ?
						<Payment 
							products={productList}
							cardSubmit={onPay}
							total={total}
							shipping={shippingCost}
							error={error}
						/>
						: <Redirect to="/checkout/levering"/>
					}/>
					<Route path="kvittering" render={() => 
						<Confirmation 
							products={orderProducts} 
							name={name.current}
							total={order && order.productTotal}
							shipping={order && order.shippingTotal}
						/>
					}/>
					<Route render={() => <Redirect from="/checkout" to="/checkout/levering"/>}/>
				</RelativeSwitch>
			</div>
		</div>
	)
};

const Checkout = reduxWrapper(
	"getApiCall",
	"loadVendors",
	"loadCart",
	"loadProducts",
	"getCacheProduct",
	"getCheckoutCart",
	"getPayOrder",
	"getUserId"
)(CheckoutRender);

const Shipping = ({products = [], shipping, total, onSubmit, apiCall, vendors, shippingNext, userId}) => {
	const [addressInfo, setAddressInfo] = useState({mainAddress: {}});
	const [customOptions, setCustomOptions] = useState({});

	const customOptionProducts = mapArray(products, x => {
		const arr = (x.product.customOptions || []).filter(c => c.checkout);
		if (arr.length < 1) return null;
		return {...x, product: {...x.product, customOptions: arr}};
	});

	const sortedProducts = partitionObject(products, ["product", "vendor"]);

	const submit = (data) => {
		let valid = customOptionProducts
			.every(p => p.product.customOptions
				.every(c => customOptions[p.id] && customOptions[p.id][c.id]));
		
		if (!valid) alert("Venglist udfyld alle felter");
		else onSubmit({...data, customOptions});
	};
	
	const onCustomOptionChange = (prodId, optId, value) => {
		if (moment.isMoment(value))
			value = value.format("YYYY-MM-DD HH:mm:ss");
		setCustomOptions(p => ({...p, [prodId]: {...p[prodId], [optId]: value}}))
	};
	
	useEffect(() => {
		if(userId){
			apiCall("GET", "/user/me/address")
				.then(data => data && setAddressInfo(p => ({
						...p,
						mainAddress: {
							name: p.mainAddress.name,
							address: data.address,
							city: data.city,
							zipcode: data.zipcode
						}
					})
				));
			
			apiCall("GET", "/user/me")
				.then(data => data && setAddressInfo(p => ({
					...p,
					mainAddress: {...p.mainAddress, name: data.fullname},
					email: data.email,
					phone: data.phone,
					savedCard: data.cardLast4
				})
			));
		}
	}, [userId]);

	const goToPayment = () => {
		if(shippingNext()){
			document.getElementById("address-submit").click(); 
			window.scrollTo(0,0);
		}
	}
	
	return (
		<Fragment>

			<h1 className="title-m">Levering og tidspunkt</h1>
			<StepsSlider pos={1}/>

			<div className="checkoutContent">
				<div className="checkoutMainContent">
					<section className="checkoutSection">
						<div className="sectionTitle">
							<h1 className="title-m">Dine oplysninger</h1>
							<span className="infoText">Indtast dine fakturerings- og leveringsinformationer</span>
						</div>
						<ValidateForm values={addressInfo} onChange={setAddressInfo} onSubmit={submit}>
							<div id="shippingInfo" className="sectionContent">
								<span className="addressTitle">Leveringsadresse</span>
								<FormGroup name="mainAddress">
									<FormInput name="name" title="Navn" req/>
									<FormInput name="address" title="Adresse" req/>
									<div className="postNrAndTown">
										<FormInput name="zipcode" title="Postnr" req/>
										<FormInput name="city" title="By" req/>
									</div>
								</FormGroup>
								<FormInput name="email" rules="email" type="email" req/>
								<FormInput name="phone" rules="phone" title="Mobil telefon" type="phone" req/>
								<Checkbox name="separateBillingAddress" title="Alternativ faktureringsadresse"/>
								{addressInfo.separateBillingAddress &&
								<FormGroup name="billingAddress">
									<span className="altAddress addressTitle">Faktureringsadresse</span>
									<FormInput name="name" title="Navn" req/>
									<FormInput name="address" title="Adresse" req/>
									<div className="postNrAndTown">
										<FormInput name="city" title="By" req/>
										<FormInput name="zipcode" title="Postnummer" req/>
									</div>
								</FormGroup>
								}
							</div>
							<Button submit id="address-submit" style={{display: "none"}}/>
						</ValidateForm>
					</section>

					{ 
						customOptionProducts.length > 0 &&
						<section className="checkoutSection">
							<div className="sectionTitle">
								<h1 className="title-m">Vælg tidspunkt</h1>
							</div>
							<div className="sectionContent">
								{customOptionProducts.map(x => (
									<div key={x.id} className="footerProductWrapper">
										<div className="prodInfo">
											<img src={getImage(x.product.thumbnail)} alt={x.product.name}/>
											<div className="prodText">
												<span className="prodName">{x.qty || x.qtyOrdered || 0} x {x.product.name}</span>
												<span className="prodPrice">A {formatPrice(x.price)} kr</span>
											</div>
										</div>

										<div>
											<div className="prodSettings">
												{x.product.customOptions.map(c => (
													<CustomOption {...c} value={(customOptions[x.id] && customOptions[x.id][c.id]) || null} key={c.id}
																				onChange={val => onCustomOptionChange(x.id, c.id, val)}/>
												))}
											</div>

											<div className="prodTotalPrice">
												<span>{formatPrice(Number(x.price) * (x.qty || x.qtyOrdered || 0))} kr</span>
											</div>
										</div>

									</div>
								))}
							</div>
						</section>
					}

					<section className="checkoutSection">
						<div className="sectionTitle">
							<h1 className="title-m">Din kurv</h1>
						</div>
						<div id="cartWrapper">
							{mapObjectToArray(sortedProducts, (products, k, i) =>
								<Package
								key={k}
								vendor={vendors[k]}
								products={products}
								options={(vendors[k] || {shipping: []}).shipping}
								/>
								)}
						</div>
					</section>
				</div>

				<div className="statusBox">
					<div className="statusBoxContent">
						<div className="statusHeader">
							<h1 className="title-m">Total</h1>
							<img src={CartIcon} alt="Kurv"/>
						</div>
						<PriceSummery total={total} shipping={shipping}/>
					</div>
					<BrowserView renderWithFragment>
						<Button	onClick={() => goToPayment()}>Gå til betaling</Button>
					</BrowserView>
				</div>
				<MobileView renderWithFragment>
					<Button	onClick={() => goToPayment()}>Gå til betaling</Button>
				</MobileView>
			</div>

		</Fragment>
	);
};

//<editor-fold desc="Shipping">
const PackageRender = ({options, products, deleteCart, updateCart, vendor}) => {
	
	const [showProducts, setShowProducts] = useState(true);
	
	const vendorNameChange = (name) => {
		switch (name) {
			case "Bevco":
				return "Drikkevarer"
			case "Ctiparty":
				return "Dekoration mm."
			case "Alletiders Party Service":
				return "Serviceudlejning"
			case "Kokken og Jomfruen":
				return "Kokken & Jomfruen"
			case "Limofyn":
				return "Limosine"
			case "Euroflorist":
				return "Blomster"
			case "Smageriet":
				return "Smageriet"
			case "Party King":
				return "Diverse "
			case "Lydudlejning":
				return "Lydudlejning"
			default:
				return name;
		}
	}

	return (
			<div className="packageContainer">
				<div className="packageTop">
					<div className="endBorder"/>
					<div className="packageTitle">
						<div className="dropBtn" onClick={() => setShowProducts(p => !p)}>
							<span>{vendor ? vendorNameChange(vendor.name) : "Pakke"}</span>
							<img className={showProducts ? "flipped" : ""} src={DropArrowIcon} alt="dropDown"/>
						</div>
					</div>
				</div>

				<Collapse in={showProducts}>
					<div>
						{products.map((x, i) =>
							<div key={i} className="footerProductWrapper">
								<div className="prodInfo">
									<img src={getImage(x.product.thumbnail)} alt={x.product.name}/>
									<div className="prodText">
										<span className="prodName">{x.qty} x {x.product.name}</span>
										<span className="prodPrice">A {formatPrice(x.price)} kr</span>
									</div>
								</div>
								
								<div>
									<div className="prodSettings">
										<QtySelector value={x.qty} min={x.section === 9 ? 10 : 1} max={x.product.qty}
																onChange={val => updateCart({id: x.id, data: {qty: val}})}/>
										<span onClick={() => deleteCart({id: x.id})}>Fjern</span>
									</div>
									<div className="prodTotalPrice">
										<span>{formatPrice(Number(x.price) * x.qty)} kr</span>
									</div>
								</div>
							</div>
						)}
						<span className="shipping">Levering: {options[0] && formatPrice(options[0].price)} kr</span>
					</div>
				</Collapse>
			</div>
	);
};

export const Package = reduxWrapper("deleteCart", "updateCart")(PackageRender);
//</editor-fold>

const Payment = ({products, cardSubmit, total, shipping, error}) => (
	<Fragment>
		<h1 className="title-m">Betaling</h1>
		<StepsSlider pos={2}/>

		<div className="checkoutContent">
			<div className="checkoutMainContent">
				<section className="checkoutSection">
					<div className="sectionTitle">
						<h1 className="title-m">Betaling</h1>
					</div>	
					<div className="sectionContent cardInfo">
						{error && <span className="error">Det ser ud til du tastede forkert. Prøv igen.</span>}
						<ValidateForm onSubmit={(data) => {
							cardSubmit(data);
							window.scrollTo(0, 0);
						}}>
							<FormInput name="cardNumber" title="Kortnummer" req/>
							<FormInput name="expiry" title="Udløbsdato(MM/YY)" req formatChange={formatExpiry}/>
							<FormInput name="cvs" title="CVS Kode" req/>
							<div className="paySection">
								<Checkbox
									name="tos"
									title={
										<Fragment>Jeg accepterer
											<a target="_blank" href={"/info/handelsbetingelser"}> Handelbetingelserne</a>
										</Fragment>
									}
								/>
								<Button id="checkout-submit" className="btnGreen" submit style={{display: "none"}}>GENNEMFØR BETALING</Button>
							</div>
						</ValidateForm>
						<div className="sslContainer">
							<img src={SslIcon} alt="SSL"/>
							<p>
								Dine betalings oplysninger er sikret med en SSL kryptering, den sikreste
								på marked. Der sikre at dine kort oplysninger ikke vil blive tilgænge
								for andre.
							</p>
						</div>
					</div>
				</section>

				<section className="checkoutSection">	
					<div className="sectionTitle">
						<h1 className="title-m">Overblik</h1>
					</div>
					<div className="sectionContent">
						{products.map((x, i) =>
							<div key={i} className="footerProductWrapper">
								<div className="prodInfo">
									<img src={getImage(x.product.thumbnail)} alt={x.product.name}/>
									<div className="prodText">
										<span className="prodName">{x.qty || x.qtyOrdered || 0} x {x.product.name}</span>
										<span className="prodPrice">A {formatPrice(x.price)} kr</span>
									</div>
								</div>
								<div className="prodTotalPrice">
									<span>{formatPrice(Number(x.price) * (x.qty || x.qtyOrdered || 0))} kr</span>
								</div>
							</div>
						)}
					</div>
				</section>
			</div>

			<div className="statusBox">
				<div className="statusBoxContent">
					<div className="statusHeader">
						<h1 className="title-m">Total</h1>
						<img src={CartIcon} alt="Kurv"/>
					</div>
					<PriceSummery total={total} shipping={shipping}/>
				</div>
				<BrowserView renderWithFragment>
					<Button onClick={() => document.getElementById("checkout-submit").click()}>
						GENNEMFØR BETALING
					</Button>
				</BrowserView>
				<div className="paymentIcons">
					<img src={visaIcon} alt="Visa"/>
					<img src={mastercardIcon} alt="Mastercard"/>
					<img src={mobilepayIcon} alt="Mobilepay"/>
				</div>
			</div>
			<MobileView renderWithFragment>
				<Button onClick={() => document.getElementById("checkout-submit").click()}>
					GENNEMFØR BETALING
				</Button>
			</MobileView>
		</div>
	</Fragment>
)

const Confirmation = ({products, name, total, shipping}) => (
	<Fragment>
		<h1 className="title-m">Kvittering</h1>
		<StepsSlider pos={3}/>

		<div className="checkoutContent">
			<div className="checkoutMainContent">
				<section className="checkoutSection">
					<div className="sectionTitle">
						<h1 className="doneTitle title-m">Sådan {name}, din bestilling er gennemført!</h1>
						<span className="infoText">Tak fordi du valgte festen.dk</span>
					</div>
					<div className="sectionContent">
						{products.map((x, i) =>
							<div key={i} className="footerProductWrapper">
								<div className="prodInfo">
									<img src={getImage(x.product.thumbnail)} alt={x.product.name}/>
									<div className="prodText">
										<span className="prodName">{x.qty || x.qtyOrdered || 0} x {x.product.name}</span>
										<span className="prodPrice">{formatPrice(x.price)}</span>
									</div>
								</div>
								<div className="prodTotalPrice">
									<span>{formatPrice(Number(x.price) * (x.qty || x.qtyOrdered || 0))} kr</span>
								</div>
							</div>
						)}
					</div>
				</section>
			</div>

			<div className="statusBox">
				<div className="confirmBox">
					<img src={CheckWhiteIcon} alt="Gennemført"/>
					<h1>Gennemført</h1>
				</div>
				<div className="statusBoxContent">
					<div className="statusHeader">
						<h1 className="title-m">Total</h1>
						<img src={CartIcon} alt="Kurv"/>
					</div>
					<PriceSummery total={total} shipping={shipping}/>
				</div>
			</div>
		</div>
	</Fragment>
)

const StepsSlider = ({pos}) => {	
	return (
		<div className="stepsWrapper">
			<div className="stepsContainer">
				<div className="textContainer">
					{mapObjectToArray(steps, (x, k) => (
						<div className={"milestone" + (pos < k ? " fade" : "")} key={k}>
							<span>{k}</span>
							<span>. {x}</span>
						</div>
					))}
				</div>
				
				<div className="ringsContainer">
					{mapObjectToArray(steps, (x, k) => (
						<Fragment key={k}>
							<div className={"circkleWrapper" + (pos > k ? " done" : (pos < k ? " fade" : ""))}>
								{pos > k && <img src={CheckWhiteIcon} alt="Flueben"/>}
							</div>
							{k < objLength(steps) && <div className={"connectorLine" + (pos <= k ? " fade" : "" )}/>}
						</Fragment>
					))}
				</div>
			
			</div>
		</div>
	)
};

export const PriceSummery = ({total, shipping}) => (
	<div className="priceSummary">
		<div className="subTotal">
			<span>Subtotal</span>
			<span>{formatPrice(total - total * 0.2)} kr</span>
		</div>
		
		<div className="shipping">
			<span>Levering</span>
			<span>{formatPrice(shipping)} kr</span>
		</div>
		
		<div className="moms">
			<span>Moms</span>
			<span>{formatPrice(total * 0.2)} kr</span>
		</div>
		
		<div className="total">
			<span>I alt</span>
			<span>{formatPrice(Number(total) + Number(shipping))} kr</span>
		</div>
	</div>
);

const formatExpiry = (value) => {
	return (value || "")
		.replace(/[^\d/]/, "")
		.replace(/(\d\d)(\d\d?)/, "$1/$2")
		.substr(0, 5);
};
//</editor-fold>

export default Checkout;


// WEBPACK FOOTER //
// ./src/components/views/checkout.js